// searchsync-favorites-filter-component.js

import { getInstance, registerWidget } from './searchsync-create-instance';
import { configure } from 'instantsearch.js/es/widgets';

/**
 * "Only show my favorites" toggle (Phase 5, PLAN.md §2.3/§3 Phase 5).
 *
 * Reuses the existing preselect/custom-filter machinery already proven by
 * searchsync-enrollment-status-rl-component.js: a dedicated Configure widget per index, added/
 * removed as the checkbox toggles, carrying an objectID-IN filter built from the current user's
 * favorited posts' REAL objectID strings.
 *
 * Where the ids come from: includes/enqueue/class-algolia-component-enqueuer.php exposes them
 * under the page-wide `user_data.favorites` payload key (NOT a per-widget-id entry, unlike most
 * other widgets here — every instance of this widget on one page filters the SAME index against
 * the SAME favorites list, so there is nothing per-instance to look up), shaped as
 * `{ ids: [...postIds], object_ids: [...objectIdStrings], truncated: bool, total: int }`
 * (Context_Serializer's cap+flag contract, plus the objectID strings this widget actually
 * filters on). 'object_ids' — NOT 'ids' — is what this widget uses: a favorited post's indexed
 * objectID is `"{$post->post_type}#{$post->ID}"` (see includes/algolia/post-to-record.php), and
 * favorites can span ANY post type (unlike LearnDash's enrolled_course_ids, which is always
 * 'sfwd-courses#<id>' and so searchsync-enrollment-status-rl-component.js can hardcode that one
 * prefix) — only the server knows each favorited id's actual post type, so the JS layer must not
 * guess a single hardcoded prefix here.
 *
 * objectID filtering via facetFilters, uniformly across every backend (verified empirically
 * against real Algolia-shaped clients while building tests/e2e/favorites.spec.js):
 * - Algolia: `objectID` is filterable via facetFilters with no per-index settings step.
 * - Meilisearch: `@meilisearch/instant-meilisearch`'s InstantSearch-request translation only
 *   maps facetFilters (and numericFilters) onto Meilisearch's native `filter` syntax — it does
 *   NOT translate the raw Algolia `filters` STRING parameter at all (confirmed by inspecting
 *   node_modules/@meilisearch/instant-meilisearch/dist/index.js — only `facets`,
 *   `attributesToSnippet`, `numericFilters`, and `facetFilters` params are read). 'objectID'
 *   ALSO needs to be in the index's 'filterableAttributes' (Meilisearch rejects a filter on any
 *   attribute not explicitly declared there, unlike Algolia) — this is a per-index admin/site
 *   opt-in via the 'tangible_searchsync_index_settings_payload' filter (see
 *   src/Providers/bootstrap.php's docblock for why this is intentionally NOT wired in globally,
 *   and tests/e2e/fixtures/favorites-setup.php's Meilisearch describe block for the documented
 *   example), not something this JS file or the PHP provider can force on its own.
 * - Local: Search_Controller only implements InstantSearch's `facetFilters` ARRAY syntax at all
 *   (never the `filters` STRING syntax — see src/Local/Rest/Search_Controller.php's docblock),
 *   mapped onto Local_Backend::search()'s filter['objectID'] special case (a dedicated
 *   `documents.object_id` column match, since 'objectID' is never in the facet side table — see
 *   that class's docblock).
 *
 * facetFilters is therefore the one shape that works identically everywhere; the earlier
 * revision of this file branched on `filters` (string) for Algolia/Meilisearch vs. facetFilters
 * for Local — that branch was wrong for Meilisearch specifically and has been removed.
 */

// includes/enqueue/class-algolia-component-enqueuer.php encodes its whole payload with
// JSON_FORCE_OBJECT (see that class's enqueue_component()), which forces EVERY nested array
// (not just the top level) to decode as a plain JS object ({"0": "a", "1": "b"}) rather than a
// real array — same reason searchsync-enrollment-status-rl-component.js has its own
// normalizeToArray() helper. Object.values() on a real array is also a harmless no-op, so this
// is safe regardless of which shape actually arrives.
const normalizeToArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'object') return Object.values(val);
  return [val];
};

function applyFavoritesFilter({ search, configureWidgetRef, checked, favoriteObjectIds }) {
  // Remove any previously-added dedicated Configure widget for this index first, so toggling
  // off cleanly restores the unfiltered result set (mirrors
  // searchsync-enrollment-status-rl-component.js's applyPseudoFilterForContainer()).
  if (configureWidgetRef.current) {
    try {
      search.removeWidgets([configureWidgetRef.current]);
    } catch (_) {
      // no-op: widget may already be detached.
    }
    configureWidgetRef.current = null;
  }

  if (!checked || !Array.isArray(favoriteObjectIds) || favoriteObjectIds.length === 0) {
    // Unchecked, or the user has no favorites to filter by (empty list): no pseudo filter
    // applied. An empty favorites list intentionally does NOT hide every result (unlike
    // searchsync-enrollment-status-rl-component.js's "no ids -> impossible match" convention) —
    // an empty "my favorites" view showing nothing at all, with no further explanation, is a
    // worse UX than the checkbox simply having no effect until the user favorites something.
    // (search.helper is null until the instance start()s — the default_on init path runs
    // before that, and the initial search needs no explicit trigger anyway.)
    if (checked && search.helper) search.helper.search();
    return;
  }

  // Array-of-arrays = AND of ORs (InstantSearch's facetFilters convention, see
  // src/Local/Rest/Search_Controller.php's docblock): a single inner OR-group of
  // 'objectID:<id>' entries.
  const pseudoConfigure = configure({
    facetFilters: [favoriteObjectIds.map((objectId) => `objectID:${objectId}`)],
  });
  search.addWidgets([pseudoConfigure]);
  configureWidgetRef.current = pseudoConfigure;

  // Pre-start (default_on init path): the Configure widget just added is included in the
  // instance's first search automatically; search.helper only exists after start().
  if (search.helper) search.helper.search();
}

function initFavoritesFilterWidget() {
  const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded);
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);

  const favoritesPayload = (tgblAlgoliaData.user_data && tgblAlgoliaData.user_data.favorites) || {};
  // Mutable on purpose: initFavoritesLiveResync() splices fresh server state into this same
  // array reference, so every instance closure below sees updates without re-wiring.
  const favoriteObjectIds = normalizeToArray(favoritesPayload.object_ids);

  const elements = document.querySelectorAll("[data-filter-type='favoritesFilter']");
  const instances = [];

  elements.forEach((element) => {
    const indexName = element.getAttribute('data-index');
    if (!indexName) return;

    const search = getInstance(indexName);
    if (!search) return;

    const checkbox = element.querySelector('.tgbl-favorites-filter__checkbox');
    if (!checkbox) return;

    // Each widget instance tracks its own Configure widget reference (a page can have this
    // widget on more than one index).
    const configureWidgetRef = { current: null };

    checkbox.addEventListener('change', () => {
      applyFavoritesFilter({
        search,
        configureWidgetRef,
        checked: checkbox.checked,
        favoriteObjectIds,
      });
    });

    // default_on: the PHP template rendered the checkbox already checked (see
    // FavoritesFilterShortcodeGenerator) — apply the filter before the instance starts so
    // the very first search is already favorites-only.
    if (checkbox.checked) {
      applyFavoritesFilter({
        search,
        configureWidgetRef,
        checked: true,
        favoriteObjectIds,
      });
    }

    instances.push({ search, configureWidgetRef, checkbox });
    registerWidget(element);
  });

  if (instances.length > 0) {
    initFavoritesLiveResync({ tgblAlgoliaData, elements, instances, favoriteObjectIds });
  }
}

/**
 * Live resync: make the filter react to on-page favorite/unfavorite toggles without a
 * page reload.
 *
 * INTERIM IMPLEMENTATION — see UPSTREAM-ISSUES.md issue (c). The Favorites plugin fires no
 * client-side action after a toggle; its buttons dispatch into per-button @wordpress/data
 * redux stores. So the trigger here is registry-wide wp.data.subscribe() (on a frontend
 * page, Favorites' stores are in practice the only dispatchers), debounced. On each
 * (debounced) trigger the AUTHORITATIVE list is re-fetched from
 * GET searchsync/v1/user-context/favorites (endpoint + nonce arrive via this widget's
 * 'favorites_refresh' registered data) and, only if it actually differs from the current
 * list, spliced into the shared favoriteObjectIds array and re-applied on every instance
 * whose checkbox is checked. The diff makes init-time dispatch noise (INITIAL_STATE per
 * button) a cheap no-op.
 *
 * REFACTOR PATH once the upstream hook lands: replace the wp.data.subscribe() trigger with
 * addAction('tangible-favorites-interaction-changed', 'searchsync', scheduleResync) —
 * everything else (re-fetch, diff, re-apply) stays, the server list remains the source of
 * truth.
 *
 * Degrades silently: no refresh config (widget data missing), or no wp.data on the page
 * (no Favorites buttons anywhere) -> today's static behavior.
 */
function initFavoritesLiveResync({ tgblAlgoliaData, elements, instances, favoriteObjectIds }) {
  const refresh = Array.from(elements)
    .map((el) => (tgblAlgoliaData[el.getAttribute('data-id')] || {}).favorites_refresh)
    .find((r) => r && r.endpoint);
  if (!refresh) return;

  let timeoutId = null;
  let inFlight = false;
  let rerunAfter = false;

  const resync = async () => {
    if (inFlight) {
      // A toggle landed while a fetch was already out — run once more when it settles so
      // we never end on stale state.
      rerunAfter = true;
      return;
    }
    inFlight = true;
    try {
      const response = await fetch(refresh.endpoint, {
        credentials: 'same-origin',
        headers: refresh.nonce ? { 'X-WP-Nonce': refresh.nonce } : {},
      });
      if (!response.ok) return;
      const fresh = normalizeToArray((await response.json()).object_ids);

      const key = (list) => list.slice().sort().join(' ');
      if (key(fresh) === key(favoriteObjectIds)) return;

      favoriteObjectIds.splice(0, favoriteObjectIds.length, ...fresh);

      instances.forEach(({ search, configureWidgetRef, checkbox }) => {
        if (!checkbox.checked) return;
        applyFavoritesFilter({ search, configureWidgetRef, checked: true, favoriteObjectIds });
      });
    } catch (_) {
      // Network hiccup: keep the current list; the next toggle triggers another attempt.
    } finally {
      inFlight = false;
      if (rerunAfter) {
        rerunAfter = false;
        resync();
      }
    }
  };

  const scheduleResync = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(resync, 500);
  };

  const subscribe = () => {
    if (!window.wp || !window.wp.data || typeof window.wp.data.subscribe !== 'function') {
      return false;
    }
    window.wp.data.subscribe(scheduleResync);
    return true;
  };

  // wp.data ships with the Favorites bundles, which may enqueue after this one — retry once
  // everything has loaded before giving up.
  if (!subscribe()) {
    window.addEventListener('load', subscribe, { once: true });
  }
}

export { initFavoritesFilterWidget };
